import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders,
  HttpParams,
  HttpResponse
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, combineLatest } from 'rxjs';
import {
  catchError,
  concatMap,
  map,
  publishReplay,
  refCount
} from 'rxjs/operators';

import { RestError } from '../error/rest.error';
import { TokenHeader } from './rest.constants';
import { GenericRequest } from './rest.generic-request.interface';
import { RestRequestOptions } from './rest.request-options.interface';
import { RpcService } from './rpc.service';
import { TokenService } from '../../authentication/token.service';

@Injectable()
export class RestService {
  private endPoints$: Observable<any>;

  constructor(
    private tokenService: TokenService,
    private http: HttpClient,
    private rpcService: RpcService
  ) {}

  public query(
    endpoint: string,
    resource: string,
    requestOptions?: RestRequestOptions
  ): Observable<any> {
    const request: GenericRequest = {
      endpoint: endpoint,
      resource: resource,
      method: 'get'
    };

    if (requestOptions) {
      request.method = requestOptions.method;
      request.body = requestOptions.body;
      request.token = requestOptions.token;
      request.params = requestOptions.params;
      request.context = requestOptions.context;
    }

    return this.request(request);
  }

  public get(
    endpoint: string,
    resource: string,
    id: string | number,
    requestOptions?: RestRequestOptions
  ): Observable<any> {
    const request: GenericRequest = {
      endpoint: endpoint,
      resource: this.joinUrl(resource, id),
      method: 'get'
    };

    if (requestOptions) {
      request.method = requestOptions.method;
      request.body = requestOptions.body;
      request.token = requestOptions.token;
      request.params = requestOptions.params;
    }

    return this.request(request);
  }

  public remove(
    endpoint: string,
    resource: string,
    id: string | number,
    requestOptions?: RestRequestOptions
  ): Observable<any> {
    const request: GenericRequest = {
      endpoint: endpoint,
      resource: this.joinUrl(resource, id),
      method: 'delete'
    };

    if (requestOptions) {
      request.method = requestOptions.method;
      request.body = requestOptions.body;
      request.token = requestOptions.token;
      request.params = requestOptions.params;
    }

    return this.request(request);
  }

  public save(
    endpoint: string,
    resource: string,
    body: any,
    requestOptions?: RestRequestOptions
  ): Observable<any> {
    const request: GenericRequest = {
      endpoint: endpoint,
      resource: resource,
      method: 'post',
      body: body
    };

    if (requestOptions) {
      request.method = requestOptions.method;
      request.token = requestOptions.token;
      request.params = requestOptions.params;
    }

    return this.request(request);
  }

  private request(request: GenericRequest): Observable<any> {
    return combineLatest(
      this.getEndpoint(request.endpoint),
      this.tokenService.getToken$()
    ).pipe(
      concatMap(([endpoint, token]: [string, string]) => {
        const headers: HttpHeaders = this.generateHeaders(
          request.token || token,
          request.context || this.tokenService.context
        );
        const req: any = {
          headers: headers,
          method: request.method,
          body: {},
          search: ''
        };
        const url: string = this.joinUrl(endpoint, request.resource);

        if (request.params) {
          req.search = this.generateSearchParams(request.params);
        }
        if (request.body) {
          req.body = request.body;
        }

        return this.http
          .request(req.method, url, {
            headers,
            body: request.body,
            params: req.search
          })
          .pipe(
            map((response: HttpResponse<Body>) => {
              return response;
            }),
            catchError((response: HttpErrorResponse) => {
              throw new RestError(response.error);
            })
          );
      })
    );
  }

  private getEndpoints(): Observable<any> {
    if (!this.endPoints$) {
      this.endPoints$ = this.rpcService.makeManagerCall('getEndpoints').pipe(
        publishReplay(1),
        refCount()
      );
    }
    return this.endPoints$;
  }

  private getEndpoint(endpoint: string): Observable<string> {
    return this.getEndpoints().pipe(
      map((endpoints: string) => {
        return endpoints[endpoint] || '';
      })
    );
  }

  private generateHeaders(token?: string, context?: string): HttpHeaders {
    const headerObject: any = {
      'Content-Type': 'application/json'
    };
    if (token) {
      headerObject[TokenHeader] = token;
    } else if (this.tokenService.hasToken()) {
      headerObject[TokenHeader] = this.tokenService;
    }
    if (context) {
      headerObject['X-Resto-Context'] = context;
    }
    return new HttpHeaders(headerObject);
  }

  private joinUrl(baseUrl: string, extendUrl: string | number): string {
    let append: string = extendUrl.toString();

    if (baseUrl.slice(-1) !== '/' && append.substring(0, 1) !== '/') {
      append = '/' + append;
    }

    return baseUrl + append;
  }

  private generateSearchParams(params: any): HttpParams {
    const searchParams: HttpParams = new HttpParams();
    if (Array.isArray(params)) {
      for (let i = 0; i < params.length; i++) {
        searchParams.set(`p${i}`, params[i]);
      }
    } else if (typeof params === 'object') {
      for (const p in params) {
        if (params.hasOwnProperty(p)) {
          searchParams.set(p, params[p]);
        }
      }
    }
    return searchParams;
  }
}
